/**
 * Parent Email Modal
 * Shown to teens after they verify their age to collect parent email
 * for consent verification.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Shield, Loader2, Check, X, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentEmailModal = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const getToken = () => localStorage.getItem("reclaim_token");
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(
        `${API_URL}/api/parent/submit-email`,
        { parent_email: email },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setSubmitted(true);
      toast.success("Verification email sent to your parent!");
      
      // Give time to see success state then close
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification email");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#4A6741] to-[#3D5A35] p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full mb-3">
                  <Shield size={14} />
                  <span className="text-xs font-medium">Parent Consent</span>
                </div>
                <h2 className="text-xl font-bold">Almost there!</h2>
                <p className="text-white/80 text-sm mt-1">
                  We need your parent to verify your account
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
                data-testid="parent-email-close-btn"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                  <Check className="w-8 h-8 text-[#4A6741]" />
                </div>
                <h3 className="text-lg font-semibold text-[#292524] mb-2">
                  Email Sent!
                </h3>
                <p className="text-[#78716C] text-sm">
                  We've sent a verification email to your parent.
                  Once they verify, you'll have full access.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center gap-3 p-4 bg-[#F9FAFB] rounded-xl mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-[#D4A373]" />
                    </div>
                    <p className="text-sm text-[#57534E]">
                      For your safety, we need a parent or guardian to verify your account.
                      They'll also get access to their own wellness journey!
                    </p>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#57534E] mb-2 block">
                      Parent's Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="parent@email.com"
                        className="pl-10"
                        required
                        data-testid="parent-email-input"
                      />
                    </div>
                    <p className="text-xs text-[#A8A29E] mt-2">
                      We'll send them a verification link
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={submitting || !email}
                    className="w-full bg-[#4A6741] hover:bg-[#3D5A35] text-white py-3"
                    data-testid="send-parent-email-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Send Verification Email"
                    )}
                  </Button>
                </form>
                
                <p className="text-xs text-center text-[#A8A29E] mt-4">
                  You can still explore some features while waiting for verification
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ParentEmailModal;
